import { parseSanitizedHtml } from "@/utils/sanitizeHtml";
import { renderFormattedContent } from "@/utils/renderFormattedContent";
import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";

import "@style/Admin.scss";
import RightTimeApi from "@/API";
import { toast } from "react-toastify";
import Enums from "@/enums";
import ProctorProvidedFieldsBar from "@/elements/ProctorProvidedFieldsBar";
import { createFieldTranslator } from "@/utils/fieldTranslation";
import {
   getLanguageDisplayName,
   getLanguageId,
   isDefaultLanguageId,
   filterSupportedLanguages,
   isSingleNonEnglishLanguageTestType,
   resolveLanguageIdFromCode,
} from "@/utils/languageUtils";

import { safeReadCurrentUserFromSession } from "@/i18n/sessionUser";
import { useOptionalI18n } from "@/i18n/I18nProvider";
import { useOptionalTestTakerI18n } from "@/i18n/TestTakerI18nProvider";
import {
   actorHasSiteSelectField,
   getEfJobIdsForRequest,
   getEfJobsForRequest,
   getJobIdsFromCsrj,
   getJobsFromCsrj,
   getSitesForRequestContext,
   getSitesFromCsrj,
   getSitesFromTestType,
   getNoSitesForJobErrorMessage,
   hasCompanySiteRoleJobsForCtt,
   hasUsableCompanySiteRoleJobsForCtt,
   hasRequiredJobFieldForActor,
   insertSyntheticSiteSelectBeforeJob,
   isSiteRequestField,
   mergeCompanySiteRoleJobsForCtt,
   needsSyntheticSiteSelect,
   resolveAutoSiteIdForRequest,
   resolveEffectiveSiteIdForRequest,
   shouldDeferNoJobsWarningUntilSiteSelected,
   isJobSelectBlockedUntilSiteChosen,
   testTypeHasProctorProvidedJobField,
   resolveDepartmentSelectOptions,
   getDepartmentSelectUiMode,
   NO_DEPARTMENTS_FOR_TEST_TYPE_MESSAGE,
   isDepartmentRequestField,
} from "@/utils/formSelectionOptions";
import {
   countVisibleTesteeRequestFields,
   findActiveRequestTestTypeByEvalComparison,
   getActiveRequestTestTypes,
} from "@/utils/requestPageHelpers";
import { omitBeLinkCoveredFields } from "@/utils/beLinkRequest";
import { apiMessage } from "@/utils/apiMessage";

export default function Request(props) {
   const params = useParams(); // Access URL parameters
   const currentUser = safeReadCurrentUserFromSession();
   const currentUserId = currentUser?.userId || currentUser?.id || null;
   const isLoggedInUserContext = !!currentUserId;

   // Properly initialize state and extract props
   const [testState, updateTestState] = useState(props?.testState || null);
   const { active, onCreate, updateSpinner, onClose, companyId, cttId, title, adminPerms, preferredEvalComparison, excludeEvalComparisons, includeEvalComparisons, lockedSiteId, lockedJobId } = props;

   const [data, updateData] = useState<any>({});
   const [messages, setMessages] = useState([]);
   const [isValid, updateValid] = useState(false);
   const [showRequiredErrors, setShowRequiredErrors] = useState(false);
   const [activeTestType, updateActiveTestType] = useState(null);
   const [loadingCsrjForCttId, setLoadingCsrjForCttId] = useState(null);
   const modalContentRef = useRef(null);
   const autoStartAttemptedRef = useRef(false);
   const loadedCsrjForCttIdsRef = useRef(new Set());
   const wasActiveRef = useRef(false);
   const [headerText, updateHeaderText] = useState(title || "Request");
   const [originalTestState, updateOriginalTestState] = useState(null);
   const adminI18n = useOptionalI18n();
   const testTakerI18n = useOptionalTestTakerI18n();
   const sharedFieldTranslations = adminI18n?.fieldTranslations ?? testTakerI18n?.fieldTranslations ?? null;
   const [fieldTranslations, setFieldTranslations] = useState([]);
   const [interfaceLanguageId, setInterfaceLanguageId] = useState(1);
   const [existingBes, setExistingBes] = useState([]);
   const [replacesTestId, setReplacesTestId] = useState(0);

   function resolveInterfaceLanguageId(nextTestState = testState) {
      if (isLoggedInUserContext) {
         const preferred = Number(currentUser?.languageId || currentUser?.defaultLanguageId || 1);
         return Number.isFinite(preferred) && preferred > 0 ? preferred : 1;
      }

      const byId = Number(nextTestState?.testData?.languageId || nextTestState?.languageId || 0);
      if (byId > 0) return byId;

      const byCode = resolveLanguageIdFromCode(nextTestState?.lang || nextTestState?.testData?.lang, nextTestState?.languages);
      return byCode > 0 ? byCode : 1;
   }

   const effectiveFieldTranslations = sharedFieldTranslations ?? fieldTranslations;
   const t = createFieldTranslator(effectiveFieldTranslations, interfaceLanguageId);

   function resolveTranslatedText(candidates, fallbackText) {
      const normalizedCandidates = Array.from(new Set(
         (Array.isArray(candidates) ? candidates : [candidates])
            .map((value) => String(value || "").trim())
            .filter(Boolean)
      ));

      if (normalizedCandidates.length === 0) return fallbackText;
      return t(normalizedCandidates[0], fallbackText);
   }

   function isSingleNonEnglishTestType() {
      return isSingleNonEnglishLanguageTestType(activeTestType?.languages);
   }

   function getSelectableLanguages() {
      const fromState = Array.isArray(testState?.languages) ? testState.languages : [];
      if (fromState.length > 0) return filterSupportedLanguages(fromState);

      const fromTestType = Array.isArray(activeTestType?.languages) ? activeTestType.languages : [];
      return filterSupportedLanguages(fromTestType);
   }

   function getDisplayFieldLabel(field) {
      const englishSource = String(field?.fieldElementName || field?.field || field?.label || "").trim();
      const sourceLabel = String(field?.label || englishSource).trim();
      const isProctorFieldContext = Boolean(testState.isProctor || field?.proctorProvided);

      if (!isLoggedInUserContext || field?.fieldElementName==="Custom") return sourceLabel;

      if (isProctorFieldContext) {
         // The company field label is what the proctor sees, in the proctor's interface language.
         return resolveTranslatedText([sourceLabel], sourceLabel);
      }

      // For testee-provided fields on single non-English tests, keep test-language labels for the testee.
      if (!field?.proctorProvided && isSingleNonEnglishTestType()) {
         if (sourceLabel && sourceLabel !== englishSource) return sourceLabel;
         return resolveTranslatedText([englishSource, sourceLabel], sourceLabel || englishSource);
      }

      // For proctor-facing labels, do not fall back to test-language labels.
      return resolveTranslatedText([englishSource], englishSource || sourceLabel);
   }
   const isSysAdmin = () => {
      const rolePerms = Enums && Enums.ROLE_PERMS ? Enums.ROLE_PERMS : null;
      const systemAdminPerm = rolePerms && rolePerms.SystemAdmin ? rolePerms.SystemAdmin : 254;
      if (!Array.isArray(adminPerms)) {
         return false;
      }
      return adminPerms.some((permission) => {
         if (typeof permission === "number") return permission === systemAdminPerm;
         if (permission && typeof permission === "object") {
            return permission.id === systemAdminPerm || permission.permId === systemAdminPerm || permission.permissionId === systemAdminPerm;
         }
         return false;
      });
   };

   function usesProctorActorJobScope() {
      if (!currentUserId || isSysAdmin()) {
         return false;
      }
      return testTypeHasProctorProvidedJobField(activeTestType);
   }

   function getCsrjScopeKey() {
      if (usesProctorActorJobScope()) {
         return "proctorActor";
      }
      const takerUserId = getTakerUserIdForFormSelection();
      return takerUserId ? `taker:${takerUserId}` : "allTakers";
   }

   function getCsrjCacheKey(cttId) {
      return `${cttId}:${getCsrjScopeKey()}`;
   }

   function getCsrjQueryString() {
      if (usesProctorActorJobScope()) {
         return "?scope=proctorActor";
      }
      const takerUserId = getTakerUserIdForFormSelection();
      return takerUserId ? `?takerUserId=${takerUserId}` : "";
   }

   function getTakerUserIdForFormSelection() {
      if (usesProctorActorJobScope()) {
         return null;
      }
      if (testState?.isProctor || testState?.requiresProctorData) {
         return null;
      }
      const testUserId = Number(testState?.testData?.userId ?? 0);
      if (testUserId > 0) {
         return testUserId;
      }
      if (currentUserId && !isSysAdmin()) {
         return currentUserId;
      }
      return null;
   }

   function getActiveCttId() {
      return activeTestType?.companyTestType?.id ?? null;
   }

   function isCsrjLoadingForActiveType() {
      const selectedCttId = getActiveCttId();
      return !!selectedCttId && loadingCsrjForCttId === selectedCttId;
   }

   /** True when CSRJ is loaded (or not required) for site/job auto-assign and validation. */
   function isCsrjReadyForActiveType() {
      const selectedCttId = getActiveCttId();
      if (!selectedCttId) {
         return false;
      }
      if (!testTypeHasProctorProvidedJobField(activeTestType)) {
         return true;
      }
      if (isCsrjLoadingForActiveType()) {
         return false;
      }
      return hasUsableCompanySiteRoleJobsForCtt(testState?.companySiteRoleJobs, selectedCttId);
   }

   function shouldSuppressNoJobsWarningWhileCsrjPending() {
      const selectedCttId = getActiveCttId();
      if (!selectedCttId || !testTypeHasProctorProvidedJobField(activeTestType)) {
         return false;
      }
      return isCsrjLoadingForActiveType()
         || !hasUsableCompanySiteRoleJobsForCtt(testState?.companySiteRoleJobs, selectedCttId);
   }

   function selectPreferredEvalTestType() {
      const preferredType = findActiveRequestTestTypeByEvalComparison(
         getRequestTestTypes(),
         preferredEvalComparison
      );
      const preferredId = preferredType?.companyTestType?.id;
      if (!preferredId) {
         return false;
      }
      updateActiveTestType(preferredType);
      updateData((prevData) => ({
         ...prevData,
         companyTestTypeId: preferredId
      }));
      return true;
   }

   useEffect(() => {
      if (!(companyId && companyId > 0 && active)) {
         wasActiveRef.current = false;
         return;
      }

      const justOpened = !wasActiveRef.current;
      wasActiveRef.current = true;

      if (testState) {
         const selectedPreferred = justOpened && selectPreferredEvalTestType();
         if (!selectedPreferred) {
            if (getRequestTestTypes().length === 1)
               updateActiveTestType(getRequestTestTypes()[0]);
            else
               handleCttParameter();
         }
      }
      else
         updateMatrixData();

      const handleGlobalKeyDown = (event) => {
         handleKeyDown(event);
      };

      document.addEventListener("keydown", handleGlobalKeyDown);

      return () => {
         document.removeEventListener("keydown", handleGlobalKeyDown);
      };
   }, [companyId, isValid, active, cttId, testState, preferredEvalComparison]);

   useEffect(() => {
      if (!active || !cttId || getRequestTestTypes().length === 0) return;
      handleCttParameter();
   }, [active, cttId, testState]);

   useEffect(() => {
      if (!active) return;
      setInterfaceLanguageId(resolveInterfaceLanguageId(testState));
   }, [active, testState, currentUser?.languageId, currentUser?.defaultLanguageId]);

   useEffect(() => {
      if (sharedFieldTranslations) {
         setFieldTranslations([]);
         return;
      }

      let isMounted = true;

      async function loadFieldTranslationsAsync() {
         if (!active) {
            if (isMounted) setFieldTranslations([]);
            return;
         }

         if (isDefaultLanguageId(interfaceLanguageId)) {
            if (isMounted) setFieldTranslations([]);
            return;
         }

         const resp = await RightTimeApi.getAsync("/FieldTranslations");
         if (isMounted) {
            setFieldTranslations(Array.isArray(resp?.jsonData) ? resp.jsonData : []);
         }
      }

      loadFieldTranslationsAsync();
      return () => {
         isMounted = false;
      };
   }, [active, isLoggedInUserContext, interfaceLanguageId, sharedFieldTranslations]);

   useEffect(() => {
      if (active && testState) {
         if (typeof props?.onRequestModalTitleTextChange === "function") {
            props.onRequestModalTitleTextChange(String(testState?.requestModalTitleText || "").trim());
         }
         const nextLanguageId = resolveInterfaceLanguageId(testState);
         const requestTableTitleText = String(testState?.requestTableTitleText || "").trim();
         var headerText = requestTableTitleText || t("Request", title || "Request");
         updateHeaderText(headerText);
         if (testState.testData?.siteId || testState.testData?.jobId) {
            updateData((prevData) => ({
               ...prevData,
               ...(testState.testData?.siteId ? { siteId: testState.testData.siteId } : {}),
               ...(testState.testData?.jobId ? { jobId: testState.testData.jobId } : {})
            }));
         }

         if (!testState.requiresProctorData) {
            if (!testState.requiresTesteeData) {
               // Don't call onCreate if we need to prompt for language
               if (onCreate && testState.testData.stateId >= Enums.TEST_STATES.PendingStart && !testState.promptForLanguage)
                  onCreate(testState);
            }
         }
         if (getRequestTestTypes().length === 1)
            updateActiveTestType(getRequestTestTypes()[0]);
         else if (!activeTestType)
            handleCttParameter();
      }
   }, [testState]);

   useEffect(() => {
      if (active && activeTestType) {
         if (!isCsrjReadyForActiveType()) {
            return;
         }
         const requestTableTitleText = String(testState?.requestTableTitleText || "").trim();
         var headerText = requestTableTitleText || t("Request", title || "Request");
         updateHeaderText(headerText);
         const cttId = activeTestType?.companyTestType?.id;
         const eligibleSites = getEligibleSitesForActiveType();
         const autoSiteId = resolveSingleAutoSiteId();
         if (autoSiteId && !testState?.testData?.siteId) {
            updateData((prevData) => ({
               ...prevData,
               siteId: autoSiteId
            }));
         }

         const eligibleJobs = getEligibleJobsForActiveType();
         if (
            eligibleJobs.length === 1 &&
            !testState?.testData?.jobId &&
            !isJobSelectBlockedUntilSiteChosen(
               relaventFields(),
               data?.siteId,
               testState?.testData?.siteId
            )
         ) {
            updateData((prevData) => ({
               ...prevData,
               job: eligibleJobs[0].name,
               jobId: eligibleJobs[0].id,
            }));
         }

         const supervisorJobs = [...(testState?.jobs ?? [])].filter((job) => job?.id);
         const hasSupervisorJobField = (activeTestType?.fields ?? []).some(
            (field) => field.fieldElementName === "Supervisor Job Select"
         );
         if (hasSupervisorJobField && supervisorJobs.length === 1 && Number(data?.supervisorJobId) !== Number(supervisorJobs[0].id)) {
            updateData((prevData) => ({ ...prevData, supervisorJobId: supervisorJobs[0].id }));
         }

         const eligibleDepartments = getEligibleDepartmentsForActiveType();
         if (eligibleDepartments.length === 1) {
            if (data?.departmentId !== eligibleDepartments[0].id) {
               updateData((prevData) => ({ ...prevData, departmentId: eligibleDepartments[0].id }));
            }
         } else if (
            data?.departmentId &&
            !eligibleDepartments.some((d) => d.id === data.departmentId)
         ) {
            updateData((prevData) => {
               const { departmentId: _removed, ...rest } = prevData;
               return rest;
            });
         }
      }
   }, [active, activeTestType, interfaceLanguageId, testState?.requestTableTitleText, testState?.companySiteRoleJobs, testState?.testData?.jobId, testState?.testData?.siteId, testState?.efTests, testState?.jobs, testState?.departments, testState?.testTypeDepartments, data?.siteId, data?.departmentId, data?.jobId, data?.supervisorJobId, loadingCsrjForCttId]);

   useEffect(() => {
      const siteId = Number(data?.siteId || testState?.testData?.siteId || lockedSiteId || 0);
      const jobId = Number(data?.jobId || testState?.testData?.jobId || lockedJobId || 0);
      const isBeSource = Number(activeTestType?.companyTestType?.evalComparison) === Enums.TEST_EVAL_COMPARISON.BE_SOURCE;
      if (!active || !testState?.isProctor || !isBeSource || !(Number(companyId) > 0) || !(siteId > 0) || !(jobId > 0)) {
         setExistingBes([]);
         setReplacesTestId(0);
         return;
      }

      let cancelled = false;
      (async () => {
         const resp = await RightTimeApi.getAsync(
            `/Company/${companyId}/Site/${siteId}/Job/${jobId}/ExistingBes`
         );
         if (cancelled) return;
         const options = resp?.ok && Array.isArray(resp.jsonData) ? resp.jsonData : [];
         setExistingBes(options);
         setReplacesTestId((current) => (
            options.some((option) => Number(option.testId) === Number(current)) ? current : 0
         ));
      })();

      return () => {
         cancelled = true;
      };
   }, [active, companyId, data?.siteId, data?.jobId, testState?.testData?.siteId, testState?.testData?.jobId, testState?.isProctor, activeTestType, lockedSiteId, lockedJobId]);

   useEffect(() => {
      if (data && testState?.testData) {
         const valid = validate();
         if (valid) {
            setShowRequiredErrors(false);
         }
      }
   }, [data]);

   useEffect(() => {
      if (typeof props?.onFieldValuesChange === "function") {
         props.onFieldValuesChange(data);
      }
   }, [data, props?.onFieldValuesChange]);

   useEffect(() => {
      if (!active || !data?.siteId || !testState?.efTests || !activeTestType) return;
      if (!isEfTestType()) return;

      const eligibleJobs = getEfJobsForRequest(
         testState.efTests,
         testState?.companySiteRoleJobs,
         getFormSelectionFilterOptions(),
         testState?.jobs
      );
      if (eligibleJobs.length === 1) {
         updateData((prevData) => ({ ...prevData, job: eligibleJobs[0].name, jobId: eligibleJobs[0].id }));
      }
   }, [active, activeTestType, data?.siteId, testState?.efTests, testState?.companySiteRoleJobs, testState?.jobs]);

   useEffect(() => {
      if (!active || !companyId || !activeTestType?.companyTestType?.id) {
         return;
      }

      const selectedCttId = activeTestType.companyTestType.id;
      const cacheKey = getCsrjCacheKey(selectedCttId);
      if (loadedCsrjForCttIdsRef.current.has(cacheKey)) {
         return;
      }

      let cancelled = false;
      setLoadingCsrjForCttId(selectedCttId);

      async function loadCompanySiteRoleJobsAsync() {
         const ret = await RightTimeApi.getAsync(
            `/CompanySiteRoleJobs/${companyId}/${selectedCttId}${getCsrjQueryString()}`
         );
         if (cancelled) {
            return;
         }

         if (!ret?.ok) {
            setLoadingCsrjForCttId((current) => (current === selectedCttId ? null : current));
            return;
         }

         const rows = Array.isArray(ret.jsonData) ? ret.jsonData : [];
         updateTestState((prevState) => {
            if (!prevState) {
               return prevState;
            }
            return {
               ...prevState,
               companySiteRoleJobs: mergeCompanySiteRoleJobsForCtt(
                  prevState.companySiteRoleJobs,
                  selectedCttId,
                  rows
               ),
            };
         });
         if (hasUsableCompanySiteRoleJobsForCtt(rows, selectedCttId)) {
            loadedCsrjForCttIdsRef.current.add(cacheKey);
         }
         setLoadingCsrjForCttId((current) => (current === selectedCttId ? null : current));
      }

      void loadCompanySiteRoleJobsAsync();
      return () => {
         cancelled = true;
         setLoadingCsrjForCttId((current) => (current === selectedCttId ? null : current));
      };
   }, [active, companyId, activeTestType?.companyTestType?.id, activeTestType?.fields, currentUserId]);

   useEffect(() => {
      const cttId = activeTestType?.companyTestType?.id;
      if (!cttId || !hasUsableCompanySiteRoleJobsForCtt(testState?.companySiteRoleJobs, cttId)) {
         return;
      }
      loadedCsrjForCttIdsRef.current.add(getCsrjCacheKey(cttId));
   }, [activeTestType?.companyTestType?.id, activeTestType?.fields, testState?.companySiteRoleJobs]);

   useEffect(() => {
      if (!active || !testState || autoStartAttemptedRef.current) return;
      if (testState.promptForLanguage && !testState.isProctor) return;
      if (testState.requiresProctorData) return;
      if (!testState.requiresTesteeData) return;
      if (countVisibleTesteeRequestFields(testState) > 0) return;
      if ((testState.testData?.stateId ?? 0) >= Enums.TEST_STATES.PendingStart) return;
      if (!isValid) return;

      autoStartAttemptedRef.current = true;
      void onCreateAsync({ preventDefault: () => {} });
   }, [active, testState, activeTestType, data, isValid]);

   if (!active) {
      return null;
   }

   function isEfTestType() {
      return activeTestType?.companyTestType?.evalComparison == Enums.TEST_EVAL_COMPARISON.EF;
   }

   function applyRequestTestTypeFilter(testTypes) {
      const include = Array.isArray(includeEvalComparisons) && includeEvalComparisons.length > 0
         ? includeEvalComparisons
         : null;
      const exclude = include
         ? (excludeEvalComparisons ?? null)
         : Array.from(new Set([...(excludeEvalComparisons ?? []), Enums.TEST_EVAL_COMPARISON.BE_SOURCE]));
      let types = getActiveRequestTestTypes(testTypes, {
         excludeEvalComparisons: exclude,
         includeEvalComparisons: include,
      });
      if (!include && types.length === 0) {
         types = getActiveRequestTestTypes(testTypes, {
            excludeEvalComparisons: excludeEvalComparisons ?? null,
            includeEvalComparisons: [Enums.TEST_EVAL_COMPARISON.BE_SOURCE],
         });
      }
      const savedTypeId = Number(
         testState?.testData?.companyTestTypeId
         || testState?.testData?.CompanyTestTypeId
         || testState?.testData?.companyTestType?.id
         || 0
      );
      const retainId = Number(cttId) || savedTypeId;
      if (!retainId || include) {
         return types;
      }
      if (types.some((tt) => Number(tt?.companyTestType?.id) === retainId)) {
         return types;
      }
      const retained = (testTypes ?? []).find((tt) => Number(tt?.companyTestType?.id) === retainId);
      const retainedActive = retained?.companyTestType?.active ?? retained?.companyTestType?.Active;
      if (!retained?.companyTestType || retainedActive === false) {
         return types;
      }
      return [retained, ...types];
   }

   function getRequestTestTypes() {
      return applyRequestTestTypeFilter(testState?.testTypes);
   }

   function getSelectableRequestTestTypes() {
      return getRequestTestTypes().filter(
         (tt) => Number(tt?.companyTestType?.evalComparison) !== Enums.TEST_EVAL_COMPARISON.BE_SOURCE
      );
   }

   function getRequestSiteContextOptions(filterByJobId = null, siteFieldProctorProvided = null) {
      const resolvedProctorProvided =
         siteFieldProctorProvided ?? inferSiteFieldProctorProvided();
      const filterOpts = getBaseFormSelectionFilterOptions();
      const serverUnionSites = resolvedProctorProvided
         ? testState?.eligibleSitesForProctorSiteSelect ?? activeTestType?.eligibleSitesForProctorSiteSelect
         : testState?.eligibleSitesForTakerSiteSelect ?? activeTestType?.eligibleSitesForTakerSiteSelect;
      const actorFields = getActorFieldsForSiteLogic();
      return {
         siteFieldProctorProvided,
         testType: activeTestType,
         userRoleSites: testState?.userAccessibleSites,
         eligibleSitesFromServer: serverUnionSites,
         companySiteRoleJobs: testState?.companySiteRoleJobs,
         filterOptions: filterOpts,
         efTests: testState?.efTests,
         isEfTestType: isEfTestType(),
         jobId: filterByJobId ?? data?.jobId ?? testState?.testData?.jobId ?? null,
         requireSitesWithJobs:
            hasRequiredJobFieldForActor(actorFields, resolvedProctorProvided)
            && isCsrjReadyForActiveType(),
         companyJobs: testState?.jobs,
      };
   }

   function inferSiteFieldProctorProvided() {
      if (testState?.isProctor && activeTestType?.fields) {
         return activeTestType.fields.some((field) => field.proctorProvided && isSiteRequestField(field));
      }
      return false;
   }

   function getEligibleSitesForActiveType(filterByJobId = null, siteFieldProctorProvided = null) {
      const resolvedProctorProvided =
         siteFieldProctorProvided ?? inferSiteFieldProctorProvided();
      return getSitesForRequestContext(
         getRequestSiteContextOptions(filterByJobId, resolvedProctorProvided)
      );
   }

   function resolveSiteIdFromSelectedJob() {
      const jobId = data?.jobId ?? testState?.testData?.jobId ?? null;
      if (!jobId) {
         return null;
      }
      const sites = getSitesFromCsrj(testState?.companySiteRoleJobs, {
         ...getFormSelectionFilterOptions(),
         lockedJobId: jobId,
      });
      return sites.length === 1 ? sites[0].siteId : null;
   }

   function resolveSingleAutoSiteId() {
      const fromJob = resolveSiteIdFromSelectedJob();
      if (fromJob) {
         return fromJob;
      }
      return resolveAutoSiteIdForRequest({
         activeTestType,
         actorFields: getActorFieldsForSiteLogic(),
         siteFieldProctorProvided: inferSiteFieldProctorProvided(),
         eligibleSites: getEligibleSitesForActiveType(),
      });
   }

   function getEligibleJobsForActiveType() {
      const filterOpts = getFormSelectionFilterOptions();
      if (isEfTestType()) {
         return getEfJobsForRequest(
            testState?.efTests,
            testState?.companySiteRoleJobs,
            filterOpts,
            testState?.jobs
         );
      }
      return getJobsFromCsrj(
         testState?.companySiteRoleJobs,
         filterOpts,
         testState?.jobs
      );
   }

   function getEligibleDepartmentsForActiveType() {
      const cttId = activeTestType?.companyTestType?.id;
      return resolveDepartmentSelectOptions({
         companyTestTypeId: cttId,
         cttFields: activeTestType?.fields,
         departments: testState?.departments,
         testTypeDepartments: testState?.testTypeDepartments,
         companySiteRoleJobs: testState?.companySiteRoleJobs,
         filterOptions: getFormSelectionFilterOptions(),
         companyJobs: testState?.jobs,
      });
   }

   function getCttDepartmentIdsForActiveType() {
      const cttId = activeTestType?.companyTestType?.id;
      if (!cttId || !Array.isArray(testState?.testTypeDepartments)) {
         return [];
      }
      return testState.testTypeDepartments
         .filter((link) => Number(link.companyTestTypeId) === cttId)
         .map((link) => Number(link.departmentId))
         .filter((id) => id > 0);
   }

   function getBaseFormSelectionFilterOptions() {
      const cttId = activeTestType?.companyTestType?.id;
      return {
         companyTestTypeId: cttId,
         selectedSiteId: data?.siteId ?? null,
         lockedSiteId: testState?.testData?.siteId ?? null,
         lockedJobId: testState?.testData?.jobId ?? null,
         selectedDepartmentId: data?.departmentId ?? null,
         cttDepartmentIds: getCttDepartmentIdsForActiveType(),
      };
   }

   function getFormSelectionFilterOptions() {
      const siteProctorProvided = resolveSiteEligibilityProctorProvided();
      const eligibleSites = getEligibleSitesForActiveType(null, siteProctorProvided);

      return {
         ...getBaseFormSelectionFilterOptions(),
         selectedSiteId: resolveEffectiveSiteIdForRequest({
            selectedSiteId: data?.siteId ?? null,
            lockedSiteId: testState?.testData?.siteId ?? null,
            eligibleSites,
            activeTestType,
            actorFields: getActorFieldsForSiteLogic(),
            siteFieldProctorProvided: siteProctorProvided,
         }),
      };
   }

   function handleCttParameter() {
      // Use cttId prop if provided
      if (cttId && getRequestTestTypes().length > 0) {
         const parsedCttId = typeof cttId === 'number' ? cttId : parseInt(cttId);
         if (parsedCttId) {
            const fullTestType = getRequestTestTypes().find(item => item.companyTestType.id === parsedCttId);
            if (fullTestType) {
               updateActiveTestType(fullTestType);
               updateData((prevData) => ({
                  ...prevData,
                  companyTestTypeId: parsedCttId
               }));

               if (!hasUsableCompanySiteRoleJobsForCtt(testState?.companySiteRoleJobs, parsedCttId)) {
                  return;
               }

               const eligibleSites = getSitesForRequestContext({
                  siteFieldProctorProvided: true,
                  testType: fullTestType,
                  userRoleSites: testState?.userAccessibleSites,
                  eligibleSitesFromServer: fullTestType?.eligibleSitesForProctorSiteSelect,
                  companySiteRoleJobs: testState.companySiteRoleJobs,
                  filterOptions: {
                     companyTestTypeId: parsedCttId,
                     lockedJobId: testState?.testData?.jobId ?? null,
                  },
                  isEfTestType: fullTestType?.companyTestType?.evalComparison === Enums.TEST_EVAL_COMPARISON.EF,
                  efTests: testState?.efTests,
                  jobId: testState?.testData?.jobId ?? null,
                  requireSitesWithJobs: hasRequiredJobFieldForActor(
                     fullTestType?.fields,
                     true
                  ),
                  companyJobs: testState?.jobs,
               });
               const proctorActorFields = (fullTestType?.fields ?? []).filter((field) => field.proctorProvided);
               const siteActorProctorProvided = proctorActorFields.some(
                  (field) => field.proctorProvided && isSiteRequestField(field)
               );
               const autoSiteId = resolveAutoSiteIdForRequest({
                  activeTestType: fullTestType,
                  actorFields: proctorActorFields,
                  siteFieldProctorProvided: siteActorProctorProvided,
                  eligibleSites,
               });
               if (autoSiteId) {
                  updateData((prevData) => ({
                     ...prevData,
                     siteId: autoSiteId
                  }));
               }
            }
         }
      }
   }

   async function updateMatrixData() {
      // Don't go retrieve the testState if it has already been loaded previously (ie passed in from Start.jsx)
      if (companyId && companyId > 0 && !testState) {

         updateSpinner(true);
         var url = "/TestStateByCompany/" + companyId;
         if(props.cttId)
            url += "?cttId=" + props.cttId;
         if(props.testId)
            url += (props.cttId ? "&" : "?") + "testId=" + props.testId;
         const ret = await RightTimeApi.getAsync(url);
         updateSpinner(false);
         if (ret && ret.ok) {
            let jsonData = withLockedSiteJob(ret.jsonData);
            // Modal create-from-CTT-row: keep TestStateByCompany CSRJ when already usable;
            // otherwise clear and lazy-load via CompanySiteRoleJobs (same as Request tab).
            if (props.cttId) {
               const parsedCttId = Number(props.cttId);
               const preloadUsable = hasUsableCompanySiteRoleJobsForCtt(
                  jsonData?.companySiteRoleJobs,
                  parsedCttId
               );
               if (!preloadUsable) {
                  jsonData = {
                     ...jsonData,
                     companySiteRoleJobs: [],
                  };
                  loadedCsrjForCttIdsRef.current = new Set();
               } else {
                  const preloadedType = jsonData?.testTypes?.find(
                     (tt) => Number(tt?.companyTestType?.id) === parsedCttId
                  );
                  const preloadScopeKey =
                     !isSysAdmin() && testTypeHasProctorProvidedJobField(preloadedType)
                        ? "proctorActor"
                        : "allTakers";
                  loadedCsrjForCttIdsRef.current = new Set([`${parsedCttId}:${preloadScopeKey}`]);
               }
            }
            updateTestState(jsonData);
            const activeTypes = applyRequestTestTypeFilter(jsonData?.testTypes);
            const preferredType = findActiveRequestTestTypeByEvalComparison(activeTypes, preferredEvalComparison);
            if(props.cttId && activeTypes.some(tt=>tt.companyTestType.id === Number(props.cttId))) {
               updateActiveTestType(activeTypes.find(tt=>tt.companyTestType.id === Number(props.cttId)));
            } else if (preferredType?.companyTestType?.id) {
               updateActiveTestType(preferredType);
               updateData((prevData) => ({
                  ...prevData,
                  companyTestTypeId: preferredType.companyTestType.id
               }));
            } else if (activeTypes.length > 0) {
               updateActiveTestType(activeTypes[0]);
            }
            if (!originalTestState)
               updateOriginalTestState(jsonData);
         }
      }
   }
   const handleKeyDown = (event) => {
      if (props?.onKeyDown)
         props.onKeyDown(event);

      if (event.key === "Enter")
         onCreateAsync(event);
   };

   function setFocusToFirstField() {
      if (modalContentRef.current) {
         // Select the first input, select, or textarea that is not disabled
         const firstField = modalContentRef.current.querySelector(
            "input:not([disabled]), select:not([disabled]), textarea:not([disabled])"
         );
         if (firstField) {
            firstField.focus();
         }
      }
   }

   function onCloseTestInfoModal() {
      // Reset all state to initial conditions (equivalent to page refresh)
      updateSpinner(true);
      loadedCsrjForCttIdsRef.current = new Set();
      updateTestState(originalTestState);
      updateData({});
      setMessages([]);
      updateValid(false);
      setShowRequiredErrors(false);
      // siteId is tracked in data now, so updateData reset above is enough.
      updateActiveTestType(null);
      updateHeaderText(t("Request", title || "Request"));

      // Call parent's onClose if it exists to reset parent state
      if (onClose)
         onClose();

      setFocusToFirstField();
      updateSpinner(false);
   }

   function getActorFieldsForSiteLogic() {
      if (testState?.isProctor && activeTestType?.fields) {
         return activeTestType.fields.filter((field) => field.proctorProvided);
      }
      return (testState?.fields ?? []).filter(
         (field) => !field.proctorProvided && field.required
      );
   }

   function isFirstOrLastNameField(field) {
      const name = String(field?.fieldElementName || "").trim().toLowerCase();
      return name === "first name" || name === "last name";
   }

   /** Taker must always supply First/Last when those fields exist (even if proctor-provided). */
   function isFieldRequiredForCurrentActor(field) {
      if (!field) return false;
      if (!testState?.isProctor && isFirstOrLastNameField(field)) return true;
      return Boolean(field.required);
   }

   function relaventFields() {

      var fields = [];
      // When showing proctor fields, use activeTestType but for testee use testState because field labels are translated there
      if (testState.isProctor && activeTestType)
         fields = activeTestType.fields.filter(f => f.proctorProvided);
      else
         fields = testState.fields.filter(f =>
            !f.proctorProvided || isFirstOrLastNameField(f)
         );
      let ret = [];
      if (fields && Array.isArray(fields) && fields.length > 0) {
         ret = fields.filter((f) => f.fieldElementName !== "Language")
         if (ret.length > 0) {
            if (testState.isProctor)
               ret = ret.filter((f) => f.proctorProvided);
            else {
               ret = ret.filter((f) =>
                  isFirstOrLastNameField(f) || (!f.proctorProvided && f.required)
               );
            }
            if (ret.some(isSiteRequestField)) {
               ret = ret.filter((field) => {
                  if (!isSiteRequestField(field)) {
                     return true;
                  }
                  if (field.synthetic) {
                     return true;
                  }
                  return getEligibleSitesForActiveType(null, Boolean(field.proctorProvided)).length >= 2;
               });
            }
            const siteActorProctorProvided = inferSiteFieldProctorProvided();
            const eligibleForActor = getEligibleSitesForActiveType(
               null,
               testState.isProctor ? true : siteActorProctorProvided,
            );
            if (needsSyntheticSiteSelect(
               getActorFieldsForSiteLogic(),
               eligibleForActor,
               siteActorProctorProvided,
               Boolean(testState?.isProctor),
               getSitesFromTestType(activeTestType).length,
            )) {
               const syntheticSiteProctorProvided = testState.isProctor || siteActorProctorProvided;
               ret = insertSyntheticSiteSelectBeforeJob(ret, syntheticSiteProctorProvided);
            }
            if (ret.some(isDepartmentRequestField)) {
               const depts = getEligibleDepartmentsForActiveType();
               const deptField = ret.find(isDepartmentRequestField);
               const mode = getDepartmentSelectUiMode({
                  hasCtt: Boolean(activeTestType?.companyTestType?.id),
                  optionCount: depts.length,
                  required: Boolean(deptField?.required),
               });
               if (mode === "hidden") {
                  ret = ret.filter((f) => !isDepartmentRequestField(f));
               }
            }
         }
      }
      return omitBeLinkCoveredFields(ret, testState?.testData?.siteId, testState?.testData?.jobId);
   }

   function withLockedSiteJob(jsonData) {
      const siteId = Number(lockedSiteId);
      const jobId = Number(lockedJobId);
      if (!(siteId > 0) && !(jobId > 0)) {
         return jsonData;
      }
      const testData = { ...(jsonData?.testData || {}) };
      if (siteId > 0) {
         testData.siteId = siteId;
      }
      if (jobId > 0) {
         testData.jobId = jobId;
      }
      return { ...jsonData, testData };
   }

   function isRequiredFieldFilled(field) {
      if (!isFieldRequiredForCurrentActor(field)) {
         return true;
      }
      switch (field.fieldElementName) {
         case "First Name":
            return !!data.firstName;
         case "Last Name":
            return !!data.lastName;
         case "Company Name":
            return !!data.company;
         case "Recruiter Initials":
         case "Initials":
            return !!(data.initials || data.recruiterInitials);
         case "Location":
            return !!data.location;
         case "Location Selection":
         case "Site Select":
            return !!data.siteId;
         case "Position":
            return !!data.position;
         case "Department Select":
            return !!data.departmentId;
         case "Supervisor Job Select":
            return !!data.supervisorJobId;
         case "Existing BE":
            return true;
         case "Job Select":
         case "Job":
            return !!data.jobId;
         case "Birth City":
            return !!data.birthCity;
         case "Birth Country":
            return !!data.birthCountry;
         case "Birth Initials":
            return !!data.birthInitials;
         case "Billing Source":
            return !!data.billingSource;
         case "Facility":
            return !!data.facility;
         case "UniqueId":
            return !!data.uniqueId;
         case "Charge":
            return !!data.charge;
         case "Parole Officer":
            return !!data.paroleOfficer;
         case "Report Recipient":
            return !!data.reportRecipient;
         case "Gender":
            return !!data.gender;
         case "Language":
         case "Internal": // boolean fields cannot be checked for validity
            return true;
         case "Supervisor":
            return !!data.reportRecipient;
         case "Custom":
            return !!(data.custom && data.custom[field.label]);
         default:
            console.log("Field validation not implemented for: ", field.field);
            debugger;
            return false;
      }
   }

   function isFieldShowingRequiredError(field) {
      return showRequiredErrors && isFieldRequiredForCurrentActor(field) && !isRequiredFieldFilled(field);
   }

   function fieldControlClassName(field) {
      return isFieldShowingRequiredError(field) ? "invalid" : undefined;
   }

   function validate() {
      const fields = relaventFields();
      let valid = true;
      if (fields && fields.length > 0) {
         fields.forEach((field) => {
            if (!field.required) {
               return;
            }
            if (!isRequiredFieldFilled(field)) {
               valid = false;
               console.log(`${field.fieldElementName} is not valid`);
            }
         });
         if (getNoSitesWarningMessage()) {
            valid = false;
         }
         if (valid) console.log("All fields are valid");
      }
      updateValid(valid);

      return valid;
   }

   async function sendRealtimeDataAsync(updates) {
      const updateEntries = Object.entries(updates);
      if (updateEntries.length > 0) {
         const [questionId, response] = updateEntries[0];
         if (questionId === undefined || questionId === null || questionId === "") {
            return;
         }
         const responseValue = response === undefined || response === null ? "" : String(response);

         const postData = {
            Code: testState?.testData?.code ?? "demo",
            LanguageId: testState?.testData?.languageId ?? 0,
            TestId: testState?.testData?.id ?? 0,
            CompanyId: companyId,
            questionId: questionId,
            response: responseValue,
            Name: "Request",
            ClientTime: new Date().toISOString(),
            When: new Date().toISOString()
         }
         const resp = await RightTimeApi.postAsync("/RealTimeResponse", postData);
      }
   }

   async function onChangeData(updates, sendRealtime = false) {
      updateData((prevData) => ({
         ...prevData,
         ...updates, // Merge the new updates into the existing data
         ...(updates?.custom
            ? { custom: { ...(prevData.custom || {}), ...(updates.custom || {}) } }
            : {}),
      }));

      if (sendRealtime)
         await sendRealtimeDataAsync(updates);
   }

   const DEFAULT_LANGUAGE_PROMPT_TEXT =
      "To make this even easier for you, please select the language you are most comfortable using to communicate with others.";

   function getLanguagePromptTextForLanguage(languageId) {
      const prompts = testState?.languagePromptTexts;
      if (prompts && typeof prompts === "object") {
         const text = prompts[languageId] ?? prompts[String(languageId)];
         const normalized = String(text || "").trim();
         if (normalized) return normalized;
      }
      return DEFAULT_LANGUAGE_PROMPT_TEXT;
   }

   function renderLanguages() {
      if (testState && testState.promptForLanguage && !testState.isProctor) {
         const selectableLanguages = getSelectableLanguages();
         return (
            <div className="languagePrompt">
               <div className="languageSelect">
                  <div className="instructions center">
                     {selectableLanguages.map((language) => {
                        const languageId = getLanguageId(language);
                        if (!languageId) return null;
                        return (
                           <div key={`language-prompt-${languageId}`} className="languagePromptText">
                              {renderFormattedContent(getLanguagePromptTextForLanguage(languageId))}
                           </div>
                        );
                     })}
                  </div>
                  <span className="buttonOptions">
                     {selectableLanguages.map((language) => {
                        const languageId = getLanguageId(language);
                        if (!languageId) return null;
                        const displayName = getLanguageDisplayName(language);
                        const abbrev = String(language?.abbrev || "").trim().toLowerCase();
                        return (
                           <button
                              key={`language-${languageId}`}
                              className="language-btn"
                              data-lang={abbrev || String(languageId)}
                              aria-label={`Select ${displayName} language`}
                              data-language={String(languageId)}
                              onClick={setLanguageAsync}
                           >
                              {displayName}
                           </button>
                        );
                     })}
                  </span>
               </div>
            </div>
         )
      } else {
         return null;
      }
   }


   async function setLanguageAsync(e) {
      e.preventDefault();

      if (!e.currentTarget.hasAttribute("data-language")) {
         console.error("Language button missing data-language attribute");
         return;
      }

      const languageId = parseInt(e.currentTarget.getAttribute("data-language"), 10);
      if (!testState?.testData?.id || !languageId) {
         console.error("Missing testId or languageId for language selection");
         return false;
      }

      try {
         updateSpinner(true);
         await sendRealtimeDataAsync({ "language": languageId.toString() });
         const testCode = testState.testData.code;
         if (!testCode) {
            toast.error("Test code not available for language selection.");
            return false;
         }
         const url = `/Test/${encodeURIComponent(testCode)}/SetLanguage/${languageId}`;
         const resp = await RightTimeApi.putAsync(url);

         if (resp && resp.ok) {
            updateTestState(resp.jsonData);
            // onCreate(resp.jsonData);
            return true;
         } else {
            toast.error("Failed to set language. Please try again.");
            return false;
         }
      } catch (error) {
         console.error("Error setting language:", error);
         toast.error("An error occurred while setting the language.");
         return false;
      } finally {
         updateSpinner(false);
      }
   }

   async function onCreateAsync(e) {
      e.preventDefault();
      let languageId = testState?.testData?.languageId || null;
      if (e.currentTarget?.hasAttribute("data-language")) {
         languageId = parseInt(e.currentTarget.getAttribute("data-language"), 10);
         updateTestState((prevState) => ({
            ...prevState,
            promptForLanguage: false
         }));
      }
      if (!validate()) {
         setShowRequiredErrors(true);
         return;
      }
      setShowRequiredErrors(false);

      const noSitesError = getNoSitesWarningMessage();
      if (noSitesError) {
         toast.error(noSitesError);
         return;
      }

      if (!languageId && activeTestType && Array.isArray(activeTestType.languages) && activeTestType.languages.length === 1)
         languageId = activeTestType.languages[0];

      // Recruiter Initials aliases Initials; prefer the shared key, keep a one-time fallback.
      const initialsValue = data.initials || data.recruiterInitials || "";

      const buildTestFieldsPayload = () => {
         const fields = relaventFields() || [];
         const getValueForField = (field) => {
            switch (field.fieldElementName) {
               case "First Name": return data.firstName;
               case "Last Name": return data.lastName;
               case "Company Name": return data.company;
               case "Position": return data.position;
               case "Job":
               case "Job Select": {
                  if (!data.jobId || !Array.isArray(testState?.jobs)) return data.position;
                  const selected = testState.jobs.find((j) => j.id === data.jobId);
                  return selected?.name || data.position;
               }
               case "Location": return data.location;
               case "Site Select":
               case "Location Selection": {
                  if (!data.siteId) return "";
                  const eligibleSites = getEligibleSitesForActiveType(null, Boolean(field?.proctorProvided));
                  const selected = eligibleSites.find((s) => Number(s.siteId) === Number(data.siteId));
                  return selected?.siteName || String(data.siteId);
               }
               case "Initials":
               case "Recruiter Initials": return initialsValue;
               case "Internal": return data.internal === true ? "true" : "false";
               case "Birth City": return data.birthCity;
               case "Birth Country": return data.birthCountry;
               case "Birth Initials": return data.birthInitials;
               case "Billing Source": return data.billingSource;
               case "Facility": return data.facility;
               case "UniqueId": return data.uniqueId;
               case "Charge": return data.charge;
               case "Parole Officer": return data.paroleOfficer;
               case "Supervisor":
               case "Report Recipient": return data.reportRecipient;
               case "Department Select": {
                  if (!data.departmentId) return "";
                  const selected = getEligibleDepartmentsForActiveType()
                     .find((d) => Number(d.id) === Number(data.departmentId));
                  return selected?.name || String(data.departmentId);
               }
               case "Supervisor Job Select": {
                  if (!data.supervisorJobId || !Array.isArray(testState?.jobs)) return "";
                  const selected = testState.jobs.find((j) => Number(j.id) === Number(data.supervisorJobId));
                  return selected?.name || String(data.supervisorJobId);
               }
               case "Gender": return data.gender;
               case "Email": return data.email;
               case "Custom": return data.custom?.[field.label];
               default: return "";
            }
         };

         return fields
            .map((field) => {
               const fieldId = Number(field.fieldId || field.testRequestFieldId || 0);
               const companyTestTypeFieldId = Number(field.id || field.companyTestTypeFieldId || 0);
               const value = getValueForField(field);
               return {
                  testRequestFieldId: fieldId,
                  companyTestTypeFieldId: companyTestTypeFieldId > 0 ? companyTestTypeFieldId : null,
                  value: value == null ? "" : String(value).trim()
               };
            })
            .filter((f) => (f.testRequestFieldId > 0 || f.companyTestTypeFieldId > 0) && f.value.length > 0);
      };

      let temp = {
         ...data,
         initials: initialsValue,
         languageId: languageId,
         companyTestTypeId: data.companyTestTypeId || activeTestType?.companyTestType.id || null,
         testFields: buildTestFieldsPayload(),
      };
      if (Number(replacesTestId) > 0) {
         temp.replacesTestId = Number(replacesTestId);
      }
      delete temp.recruiterInitials;
      if (activeTestType) {
         temp.companyId = companyId || activeTestType.companyId || activeTestType.companyTestType.companyId;
         temp.code = testState.testData.code || activeTestType.code;
         temp.testId = testState.testData.id;
         temp.id = testState.testData.id; // This is needed for sendAsync to know whether to use POST (id=0) or PUT (id>0)
         // Remove testId if it's 0 to avoid Entity Framework foreign key issues
         if (testState.testData.id === 0) {
            delete temp.testId;
            delete temp.id;
         }

         if (!data?.siteId) {
            const autoSiteId = resolveSingleAutoSiteId();
            if (autoSiteId) {
               temp.siteId = autoSiteId;
            }
         }
      }
      if (data?.siteId && data?.siteId > 0) temp.siteId = data?.siteId;
      updateSpinner(true);
      let resp = await RightTimeApi.sendAsync("/Test", temp);
      updateSpinner(false);
      if (resp && resp.ok) {
         if (checkForMessages(resp.jsonData).length === 0) {
            updateTestState(resp.jsonData);
            // Always call onCreate callback if provided, letting parent decide what to do
            if (onCreate) {
               onCreate(resp.jsonData);
            }
         }
      } else {
         const msg = resp?.jsonData?.error
            || resp?.jsonData?.msg
            || resp?.jsonData?.errorMessage
            || resp?.jsonData?.message
            || "Unable to start the questionnaire. Please try again.";
         toast.error(typeof msg === "string" ? msg : "Unable to start the questionnaire. Please try again.");
      }
   }

   function checkForMessages(jsonData) {
      let ret = [];
      if (jsonData) {
         if (jsonData.messages && jsonData.messages.length > 0) {
            // Flatten the array and ensure only strings are added
            ret = ret.concat(jsonData.messages.filter((msg) => typeof msg === "string"));
         }
         const apiText = apiMessage({ jsonData });
         if (apiText) {
            ret.push(apiText);
         }
      }
      if (ret.length > 0) {
         setMessages((prevMessages) => [...prevMessages, ...ret]);
      }
      return ret;
   }

   function renderSites(f) {
      if (isEfTestType() && testState?.testData?.siteId) {
         return null;
      }
      if (testState?.testData?.siteId && !testState.isProctor) {
         return null;
      }

      const uniqueSites = getEligibleSitesForActiveType(null, Boolean(f?.proctorProvided));

      return active && uniqueSites.length > 1 ? (
         <tr key={`field-${f.fieldElementName}-Location`}>
            <th className="label formLabel">
               {getDisplayFieldLabel(f)}
               {f.required ? <span>*</span> : null}
            </th>
            <td className="left">
               <select
                  value={data?.siteId ?? 0}
                  className={fieldControlClassName(f)}
                  aria-label={getDisplayFieldLabel(f)}
                  title={getDisplayFieldLabel(f)}
                  onChange={(e) => {
                     const selectedSiteId = parseInt(e.target.value, 10);
                     if (!selectedSiteId || selectedSiteId <= 0) {
                        onChangeData(
                           { siteId: null, jobId: null, job: null },
                           true
                        );
                        return;
                     }
                     onChangeData(
                        { siteId: selectedSiteId, jobId: null, job: null },
                        true
                     );
                  }}
               >
                  <option key="0" value="0">{t("Pick One", "Pick One")}</option>
                  {uniqueSites.map((s, i) => {
                     return (
                        <option key={"site" + i} value={s.siteId}>
                           {s.siteName}
                        </option>
                     );
                  })}
               </select>
            </td>
         </tr>
      ) : null;
   }

   function renderBooleanField(f, elementName) {
      const labelText = getDisplayFieldLabel(f);
      return (
         <tr key={`field-${f.fieldElementName}-${elementName}`}>
            <th className="label formLabel userType">
               {labelText}
               {f.required ? <span>*</span> : null}
            </th>
            <td className="left">
               <input
                  type="checkbox"
                  className="userType"
                  checked={data[elementName] || false}
                  aria-label={labelText}
                  title={labelText}
                  onChange={(e) =>
                     onChangeData({ [elementName]: data[elementName] ? !data[elementName] : true }, true)
                  }
               />
            </td>
         </tr>
      );
   }

   function renderStringField(f, elementName, maxLength = "100") {
      // debugger;
      const labelText = getDisplayFieldLabel(f);
      const customFieldKey = String(f.label || elementName || "");
      let localElementName = elementName;
      if (f.fieldElementName === "Custom") {
         localElementName = "custom";
      }
      return (
         <tr key={`field-${f.fieldElementName}-${f.fieldElementName === "Custom" ? customFieldKey : localElementName}`}>
            <th className="label formLabel">
               {labelText}
               {f.required ? <span>*</span> : null}
            </th>
            <td className="hide-lastpass left">
               <input
                  type="text"
                  className={fieldControlClassName(f)}
                  value={f.fieldElementName === "Custom" ? data.custom?.[customFieldKey] || "" : data[localElementName] || ""}
                  onChange={(e) => onChangeData(
                     f.fieldElementName === "Custom"
                        ? { custom: { [customFieldKey]: e.target.value } }
                        : { [localElementName]: e.target.value }
                  )}
                  onBlur={(e) => onChangeData(
                     f.fieldElementName === "Custom"
                        ? { custom: { [customFieldKey]: e.target.value } }
                        : { [localElementName]: e.target.value },
                     true
                  )}
                  maxLength={Number(maxLength)}
                  aria-label={labelText}
                  title={labelText}
                  data-lpignore="true"
                  autoComplete="off"
                  placeholder={labelText}
                  onKeyDown={handleKeyDown}
               />
            </td>
         </tr>
      );
   }

   function updateSupervisorJob(e) {
      const id = parseInt(e.target.value, 10);
      if (!id || Number.isNaN(id)) {
         onChangeData({ supervisorJobId: null }, true);
      } else {
         onChangeData({ supervisorJobId: id }, true);
      }
   }

   function renderSupervisorJobs(f) {
      const jobs = [...(testState?.jobs ?? [])]
         .filter((job) => job?.id)
         .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
      if (jobs.length === 1) {
         return null;
      }
      const labelText = getDisplayFieldLabel(f);
      return (
         <tr>
            <th className="label formLabel">
               {labelText}
               {f.required ? <span>*</span> : null}
            </th>
            <td className="left">
               <select
                  value={data.supervisorJobId || 0}
                  className={fieldControlClassName(f)}
                  onChange={updateSupervisorJob}
                  aria-label={labelText}
                  title={labelText}
               >
                  <option key="0" value="0">{t("Pick One", "Pick One")}</option>
                  {jobs.map((job) => (
                     <option key={"supervisorJob" + job.id} value={job.id}>{job.name}</option>
                  ))}
               </select>
            </td>
         </tr>
      );
   }

   function updateDepartment(e) {
      const id = parseInt(e.target.value, 10);
      if (!id || Number.isNaN(id)) {
         onChangeData({ departmentId: null, jobId: null, job: null }, true);
      } else {
         onChangeData({ departmentId: id, jobId: null, job: null }, true);
      }
   }

   function renderDepartments(f) {
      const cttId = activeTestType?.companyTestType?.id;
      const departments = getEligibleDepartmentsForActiveType();
      const mode = getDepartmentSelectUiMode({
         hasCtt: Boolean(cttId),
         optionCount: departments.length,
         required: Boolean(f.required),
      });

      if (mode === "hidden" || mode === "error-no-departments" || mode === "single-locked") {
         return null;
      }

      const labelText = getDisplayFieldLabel(f);
      const locked = mode === "disabled-no-ctt";
      const value = data.departmentId || 0;

      return (
         <tr>
            <th className="label formLabel">
               {labelText}
               {f.required ? <span>*</span> : null}
            </th>
            <td className="left">
               <select
                  value={value}
                  className={fieldControlClassName(f)}
                  onChange={updateDepartment}
                  disabled={locked}
                  aria-label={labelText}
                  title={labelText}
               >
                  <option key="0" value="0">{t("Pick One", "Pick One")}</option>
                  {departments.map((d) => (
                     <option key={"dept" + d.id} value={d.id}>{d.name}</option>
                  ))}
               </select>
            </td>
         </tr>
      );
   }

   function updateJob(e) {
      let id = parseInt(e.target.value, 10);
      if (!id || id <= 0) {
         const { jobId, job, ...newData } = data;
         onChangeData(newData, true);
         return;
      }
      const selectedJob = testState?.jobs?.find((j) => Number(j.id) === id);
      onChangeData(
         { jobId: id, job: selectedJob?.name ?? null },
         true
      );
   }

   function renderDisabledJobSelect(f) {
      const labelText = getDisplayFieldLabel(f);
      return (
         <tr>
            <th className="label formLabel">
               {labelText}
               {f.required ? <span>*</span> : null}
            </th>
            <td className="left">
               <select
                  value={0}
                  className={fieldControlClassName(f)}
                  disabled
                  aria-label={labelText}
                  title={labelText}
               >
                  <option key="0" value="0">
                     {t("Pick One", "Pick One")}
                  </option>
               </select>
            </td>
         </tr>
      );
   }

   function isJobSelectAwaitingSiteOrCsrj(visibleFields) {
      if (isCsrjLoadingForActiveType()) {
         return true;
      }

      const siteProctorProvided = resolveSiteEligibilityProctorProvided();
      const eligibleSites = getEligibleSitesForActiveType(null, siteProctorProvided);
      const effectiveSiteId = resolveEffectiveSiteIdForRequest({
         selectedSiteId: data?.siteId ?? null,
         lockedSiteId: testState?.testData?.siteId ?? null,
         eligibleSites,
         activeTestType,
         actorFields: getActorFieldsForSiteLogic(),
         siteFieldProctorProvided: siteProctorProvided,
      });

      if (isEfTestType() && Array.isArray(testState?.efTests) && testState.efTests.length > 0) {
         return false;
      }

      if (effectiveSiteId && effectiveSiteId > 0) {
         return false;
      }

      if (
         isJobSelectBlockedUntilSiteChosen(
            visibleFields,
            data?.siteId,
            testState?.testData?.siteId
         )
      ) {
         return true;
      }

      return needsSyntheticSiteSelect(
         getActorFieldsForSiteLogic(),
         eligibleSites,
         inferSiteFieldProctorProvided(),
         Boolean(testState?.isProctor),
         getSitesFromTestType(activeTestType).length,
      );
   }

   function renderJobs(f) {
      if (!activeTestType?.companyTestType?.id) {
         return null;
      }
      if (testState?.testData?.jobId) {
         return null;
      }

      const visibleFields = relaventFields();
      if (isJobSelectAwaitingSiteOrCsrj(visibleFields)) {
         return renderDisabledJobSelect(f);
      }

      if (!isEfTestType() && !testState.companySiteRoleJobs) {
         return null;
      }

      let jobsWithNames = [];

      if (isEfTestType()) {
         jobsWithNames = getEligibleJobsForActiveType();
         if (jobsWithNames.length === 0) {
            return null;
         }
      } else {
         jobsWithNames = getJobsFromCsrj(
            testState.companySiteRoleJobs,
            getFormSelectionFilterOptions(),
            testState.jobs
         );
      }

      if (!jobsWithNames || jobsWithNames.length <= 1) {
         return null;
      }

      const labelText = getDisplayFieldLabel(f);
      return (
         <tr>
            <th className="label formLabel">
               {labelText}
               {f.required ? <span>*</span> : null}
            </th>
            <td className="left">
               <select
                  value={data.jobId || 0}
                  className={fieldControlClassName(f)}
                  onChange={(e) => updateJob(e)}
                  aria-label={labelText}
                  title={labelText}
               >
                  <option key="0" value="0">
                     {t("Pick One", "Pick One")}
                  </option>
                  {jobsWithNames.map((j) => {
                     return (
                        <option key={"job" + j.id} value={j.id}>
                           {j.name}
                        </option>
                     )
                  })}
               </select>
            </td>
         </tr>
      );
   }

   function resolveSiteEligibilityProctorProvided(siteFieldOverride = null) {
      const inferred = siteFieldOverride ?? inferSiteFieldProctorProvided();
      if (testState?.isProctor) {
         return true;
      }
      return inferred;
   }

   function getNoSitesWarningMessage() {
      if (!activeTestType?.companyTestType?.id) {
         return "";
      }
      if (loadingCsrjForCttId === activeTestType.companyTestType.id) {
         return "";
      }
      if (
         testTypeHasProctorProvidedJobField(activeTestType) &&
         !hasCompanySiteRoleJobsForCtt(testState?.companySiteRoleJobs, activeTestType.companyTestType.id)
      ) {
         return "";
      }
      const siteEligibilityProctorProvided = resolveSiteEligibilityProctorProvided();
      const eligibleSites = getEligibleSitesForActiveType(null, siteEligibilityProctorProvided);
      return getNoSitesForJobErrorMessage(
         getActorFieldsForSiteLogic(),
         eligibleSites,
         siteEligibilityProctorProvided
      );
   }

   function getNoJobsWarningMessage() {
      const noSitesMessage = getNoSitesWarningMessage();
      if (noSitesMessage) {
         return "";
      }
      if (!activeTestType?.companyTestType?.id || testState?.testData?.jobId) {
         return "";
      }

      if (loadingCsrjForCttId === activeTestType.companyTestType.id) {
         return "";
      }

      if (shouldSuppressNoJobsWarningWhileCsrjPending()) {
         return "";
      }

      if (!testState?.companySiteRoleJobs) {
         return "";
      }

      const hasJobField = relaventFields().some(
         (field) => field.fieldElementName === "Job Select" || field.fieldElementName === "Job"
      );
      if (!hasJobField) return "";

      const siteActorProctorProvided = inferSiteFieldProctorProvided();
      const visibleFields = relaventFields();
      if (
         shouldDeferNoJobsWarningUntilSiteSelected(
            getActorFieldsForSiteLogic(),
            siteActorProctorProvided,
            data?.siteId,
            testState?.testData?.siteId,
            visibleFields
         )
      ) {
         return "";
      }

      if (
         isCsrjReadyForActiveType() &&
         needsSyntheticSiteSelect(
            getActorFieldsForSiteLogic(),
            getEligibleSitesForActiveType(null, resolveSiteEligibilityProctorProvided()),
            siteActorProctorProvided,
            Boolean(testState?.isProctor),
            getSitesFromTestType(activeTestType).length,
         ) &&
         !(data?.siteId || testState?.testData?.siteId)
      ) {
         return "";
      }

      if (isEfTestType()) {
         const uniqueJobIds = getEfJobIdsForRequest(
            testState.efTests,
            testState.companySiteRoleJobs,
            getFormSelectionFilterOptions()
         );

         if (uniqueJobIds.length === 0) {
            return testState.efTests.length === 0 ? "No related BEs have been completed." : "No jobs available";
         }
         return "";
      }

      const jobIds = getJobIdsFromCsrj(
         testState.companySiteRoleJobs,
         getFormSelectionFilterOptions(),
         testState.jobs
      );
      return jobIds.length === 0 ? "No jobs available" : "";
   }

   function getNoDepartmentsWarningMessage() {
      const cttDeptField = (activeTestType?.fields ?? []).find(isDepartmentRequestField);
      if (!cttDeptField) return "";
      const visibleToActor = testState?.isProctor
         ? cttDeptField.proctorProvided
         : !cttDeptField.proctorProvided;
      if (!visibleToActor) return "";

      const mode = getDepartmentSelectUiMode({
         hasCtt: Boolean(activeTestType?.companyTestType?.id),
         optionCount: getEligibleDepartmentsForActiveType().length,
         required: Boolean(cttDeptField.required),
      });
      return mode === "error-no-departments" ? NO_DEPARTMENTS_FOR_TEST_TYPE_MESSAGE : "";
   }

   function renderGender(f) {
      const labelText = getDisplayFieldLabel(f);
      return (
         <tr>
            <th className="label formLabel">
               {labelText}
               {f.required ? <span>*</span> : null}
            </th>
            <td className="left">
               <select
                  value={data.gender || ""}
                  className={fieldControlClassName(f)}
                  onChange={(e) => onChangeData({ "gender": e.target.value }, true)}
                  aria-label={labelText}
                  title={labelText}
               >
                  <option key="0" value="">{t("Pick One", "Pick One")}</option>
                  <option key={"gender1"} value={"male"}>male</option>
                  <option key={"gender2"} value={"female"}>female</option>
               </select>
            </td>
         </tr>
      );
   }

   function renderCustom(f) {
      return renderStringField(f, f.label, "100");
   }

   function renderField(f) {
      //console.log("Rendering field:", f);
      if (f && f.fieldElementName) {
         switch (f.fieldElementName) {
            case "First Name":
               return renderStringField(f, "firstName", "50");
            case "Last Name":
               return renderStringField(f, "lastName", "50");
            case "Company Name":
               return renderStringField(f, "company");
            case "Recruiter Initials":
               // Same DB column as Initials — keep form key aligned with API `Initials`.
               return renderStringField(f, "initials", "2");
            case "Position":
               return renderStringField(f, "position");
            case "Location":
               return renderStringField(f, "location");
            case "Location Selection":
               return renderSites(f);
            case "Site Select":
               return renderSites(f);
            case "Internal":
               return renderBooleanField(f, "internal");
            //return renderInternal(f);
            case "Initials":
               return renderStringField(f, "initials", "2");
            case "Gender":
               return renderGender(f);
            case "Department Select":
               return renderDepartments(f);
            case "Supervisor Job Select":
               return renderSupervisorJobs(f);
            case "Existing BE":
               return renderBeReplacement(f);
            case "Job Select":
            case "Job":
               return renderJobs(f);
            case "Birth City":
               return renderStringField(f, "birthCity", "100");
            case "Birth Country":
               return renderStringField(f, "birthCountry", "100");
            case "Birth Initials":
               return renderStringField(f, "birthInitials", "2");
            case "Billing Source":
               return renderStringField(f, "billingSource", "100");
            case "Facility":
               return renderStringField(f, "facility", "100");
            case "UniqueId":
               return renderStringField(f, "uniqueId", "20");
            case "Charge":
               return renderStringField(f, "charge", "100");
            case "Parole Officer":
               return renderStringField(f, "paroleOfficer", "100");
            case "Report Recipient":
               return renderStringField(f, "reportRecipient", "100");
            case "Email":
               return renderStringField(f, "email", "100");
            case "Supervisor":
               return renderStringField(f, "reportRecipient", "100");
            case "Custom":
               return renderCustom(f);
            default:
               return null;
         }
      }
      return <tr></tr>;
   }

   function onChangeTestType(e) {
      let id = parseInt(e.target.value);
      if (id) {
         const fullTestType = getRequestTestTypes().find(item => item.companyTestType.id === id);
         updateActiveTestType(fullTestType);

         updateData((prevData) => {
            const { siteId: _siteId, jobId: _jobId, job: _job, departmentId: _departmentId, supervisorJobId: _supervisorJobId, ...rest } = prevData;
            const next = { ...rest, companyTestTypeId: id };
            if (testState?.testData?.siteId) {
               next.siteId = testState.testData.siteId;
            }
            if (testState?.testData?.jobId) {
               next.jobId = testState.testData.jobId;
            }
            return next;
         });

         sendRealtimeDataAsync({ companyTestTypeId: id });
      }
   }

   function renderTestTypes() {
      if (Number(activeTestType?.companyTestType?.evalComparison) === Enums.TEST_EVAL_COMPARISON.BE_SOURCE) {
         return null;
      }
      const selectableTypes = getSelectableRequestTestTypes();
      return (
         (selectableTypes.length > 1) && (
            <tr className="testType">
               <th className="label formLabel">{t("Test Type", "Test Type")}</th>
               <td className="left">
                  <select
                     value={activeTestType?.companyTestType?.id || 0}
                     data-name="TestTypeId"
                     aria-label={t("Test Type", "Test Type")}
                     title={t("Test Type", "Test Type")}
                     className="testTypeSelect"
                     onChange={onChangeTestType}
                     disabled={selectableTypes.length === 1 || !!cttId}
                  >
                     {selectableTypes.length > 0 && (
                        <option key="tt0" value="0">Select One</option>)}
                     {selectableTypes.map((tt) => (
                        <option key={tt.companyTestType.id} value={tt.companyTestType.id}>
                           {tt.companyTestType.name}
                        </option>
                     ))}
                  </select>
               </td>
            </tr>
         )
      );
   }



   function renderBeReplacement(f) {
      if (!existingBes.length) return null;
      const configuredLabel = String(f?.label || f?.fieldElementName || "Existing BE").trim();
      const labelText = getDisplayFieldLabel({
         ...f,
         fieldElementName: configuredLabel,
         label: configuredLabel,
      });
      return (
         <tr key="be-replacement">
            <th className="label formLabel">
               {labelText}
               {f?.required ? <span>*</span> : null}
            </th>
            <td className="left">
               <select
                  value={replacesTestId || 0}
                  className={fieldControlClassName(f)}
                  onChange={(e) => setReplacesTestId(parseInt(e.target.value, 10) || 0)}
                  aria-label={labelText}
                  title={labelText}
               >
                  <option value="0">{t("New", "New")}</option>
                  {existingBes.map((be) => (
                     <option key={be.testId} value={be.testId}>{be.name}</option>
                  ))}
               </select>
            </td>
         </tr>
      );
   }

   function renderFields() {
      if (!activeTestType) return null;
      let fields = relaventFields();
      if (!fields || fields.length === 0) return null;

      if (fields) {

         // Sort fields by sequence property
         fields = fields.sort((a, b) => {
            const aSequence = a.sequence || 0;
            const bSequence = b.sequence || 0;
            return aSequence - bSequence;
         });

         const rows = fields.map((f, index) => {

            // Generate a unique key for each field using both index and field name
            const uniqueKey = `field-${f.fieldElementName}-${index}`;

            // Get the field content (which should be a tr element)
            const fieldContent = renderField(f);

            // If fieldContent is null, return null (React will filter it out)
            if (!fieldContent) return null;

            // Clone the element with a proper key if it's a valid React element
            if (React.isValidElement(fieldContent)) {
               return React.cloneElement(fieldContent, {
                  key: uniqueKey,
                  "data-field-element": f.fieldElementName || undefined,
               });
            }

            // If it's not a valid React element (unlikely), just return it
            return fieldContent;
         }).filter(Boolean);
         return rows;
      }
      return null;
   }

   if (active) {
      if (testState) {
         const noSitesWarningMessage = getNoSitesWarningMessage();
         const noJobsWarningMessage = getNoJobsWarningMessage();
         const noDepartmentsWarningMessage = getNoDepartmentsWarningMessage();
         const noTestTypesMessage = (getRequestTestTypes().length === 0)
            ? "You do not have the permissions to create a test of this type"
            : "";
         const allMessages = [
            ...messages,
            ...(noSitesWarningMessage ? [noSitesWarningMessage] : []),
            ...(noJobsWarningMessage ? [noJobsWarningMessage] : []),
            ...(noDepartmentsWarningMessage ? [noDepartmentsWarningMessage] : []),
            ...(noTestTypesMessage ? [noTestTypesMessage] : [])
         ].filter((message, index, all) => !!message && all.indexOf(message) === index);
         const hasMessages = allMessages.length > 0;
         const hasMissingRequiredFields = (relaventFields() || []).some(
            (field) => isFieldRequiredForCurrentActor(field) && !isRequiredFieldFilled(field)
         );
         const showMissingRequiredMessage = showRequiredErrors && hasMissingRequiredFields;

         if (testState.promptForLanguage && !testState.isProctor) {
            return renderLanguages();
         }

         if (testState?.testData?.stateId < 200) {
            return (
               <>
                  {props.showProctorFieldsBar !== false ? (
                     <ProctorProvidedFieldsBar
                        testState={testState}
                        valueOverrides={data}
                        mode="request"
                     />
                  ) : null}
                  <div className="requestContentStack">
                     {testState.requestPageInstructions && testState.requiresTesteeData && (
                        <div className="requestInstructions">
                           {parseSanitizedHtml(testState.requestPageInstructions)}
                        </div>
                     )}
                     <div className="tableWrapper dimensionTable requestTable">
                        <table className="nohover formTable">
                           <caption>
                             <span className="title">{headerText}</span>
                           </caption>
                           <tbody>
                              {hasMessages && (
                                 <tr>
                                    <td colSpan={2}>
                                       <div key={"messages"} className="warning">
                                          {allMessages.map((message, index) => (
                                             <p key={index}>{message}</p>
                                          ))}
                                       </div>
                                    </td>
                                 </tr>)}
                              {testState.siteImageId && (
                                 <tr>
                                    <td colSpan={2} className="center">
                                       <img
                                          src={RightTimeApi.URL("/Image/" + testState.siteImageId)}
                                          className="logo company"
                                          alt=""
                                       />
                                    </td>
                                 </tr>)}
                              {!activeTestType?.companyTestType?.id && activeTestType?.jobs?.length == 0 ?
                                 (<tr><td><span>There are no test types configured for you to create</span></td></tr>)
                                 :
                                 (
                                    <>
                                       {renderTestTypes()}
                                       {!hasMessages && renderFields()}
                                       {!hasMessages && testState?.testData?.stateId <= Enums.TEST_STATES.PendingProctorData && (
                                          <tr>
                                             <th className="actions" colSpan={2}>
                                                <div className="requestSubmitRow">
                                                   <button
                                                      className="okButton"
                                                      onClick={onCreateAsync}
                                                      disabled={hasMissingRequiredFields}
                                                   >
                                                      {t("Create", "Create")}
                                                   </button>
                                                   {showMissingRequiredMessage ? (
                                                      <span className="requiredMissing">* missing required information</span>
                                                   ) : null}
                                                </div>
                                             </th>
                                          </tr>)}
                                       {!hasMessages && testState?.testData?.stateId === Enums.TEST_STATES.PendingUserData && (
                                          <tr>
                                             <th className="actions" colSpan={2}>
                                                <div className="requestSubmitRow">
                                                   <button className="okButton" onClick={onCreateAsync}>
                                                      {testState?.startTestButtonText ?? "Start"}
                                                   </button>
                                                   {showMissingRequiredMessage ? (
                                                      <span className="requiredMissing">* missing required information</span>
                                                   ) : null}
                                                </div>
                                             </th>
                                          </tr>)}
                                    </>
                                 )}
                           </tbody>
                        </table>
                     </div>
                  </div>
               </>
            );
         }

         return null;
      } else {
         return null;
      }
   } else {
      return null;
   }
}

